import adapter from 'webrtc-adapter';
import EventEmitter from 'events';
import type { IPlayerConfig } from './types';
import WebRTCConnection from './WebRTCConnection';
import SignalController from './SignalController';
import GbsSignalController from './SignalController/GbsSignalController';
import TencentSignalController from './SignalController/TencentSignalController';
import { DISCONNECT_CODE, ErrorCode, PlayerEvents, PlayStatus } from './enums';
import { formatStatsReport, IOS_VERSION_ARR, IS_IOS, IS_SAFARI, isWebRTCAPISupported } from './utils';
import Logger from './logger';

const logger = new Logger('WebRtcPlayer');

export { isWebRTCAPISupported, PlayerEvents, SignalController, GbsSignalController, TencentSignalController };

export default class WebRtcPlayer extends EventEmitter {
  /**
   * new RTCPeerConnection 需要的参数
   */
  private rtcOptions = null;
  /**
   * createOffer 需要的参数
   */
  private offerOptions: IPlayerConfig['offerOptions'] = {
    offerToReceiveAudio: true,
    offerToReceiveVideo: true,
    sdpTransform: null,
  };
  private connectRetry = {
    /** 最大重试次数 */
    maxNum: 3,
    /** 当前重试次数 */
    curNum: 0,
    /** 重试间隔时长（秒） */
    delay: 1,
    playing: false,
  };

  private streamDecodeFail = {
    maxNum: 3,
    curNum: 0,
  };

  private streamReceiveFail = {
    maxNum: 5,
    curNum: 0,
  };

  private env = 'prod';
  /** 播放地址 */
  private playUrl: string = null;
  private playStatus: PlayStatus = PlayStatus.INIT;
  private mediaElement: HTMLMediaElement = null;
  private mediaStream: MediaStream = null;
  private signalController: SignalController = null;
  private rtcConnection: WebRTCConnection = null;
  private connectTimeout = 5000;
  private connectTimeoutId = null;
  private retryTimeoutId = null;
  private statsIntervalId = null;
  private lastStatsReport: RTCStatsReport = null;
  private fetchTimeout = 2000;

  static VERSION = '__VERSION__';

  constructor() {
    super();

    const { browser, version } = adapter.browserDetails;
    logger.info(`browser: ${browser}, version: ${version}`);
  }

  static isSupported(): boolean {
    if (IS_IOS && IS_SAFARI && IOS_VERSION_ARR.length > 0) {
      if (IOS_VERSION_ARR[0] < 11) return false;
      if (IOS_VERSION_ARR[0] === 11) {
        if (IOS_VERSION_ARR[1] < 1) return false;
        else if (IOS_VERSION_ARR[1] === 1 && IOS_VERSION_ARR[2] < 2) return false;
      }
    }
    return isWebRTCAPISupported();
  }

  getLogger() {
    return logger;
  }

  setConfig(config: Partial<IPlayerConfig> = {}) {
    const {
      env,
      logLevel,
      rtcOptions,
      offerOptions,
      connectTimeout,
      connectRetryDelay,
      maxConnectRetry,
      signalController,
      maxDecodeFailCount,
      maxReceiveFailCount,
      fetchTimeout,
      fetchSetup,
    } = config;

    if ('string' === typeof env) {
      this.env = env.toLowerCase();
    }

    if (typeof logLevel === 'string') {
      logger.setLevel(logLevel as any);
    }

    if (!!rtcOptions && typeof rtcOptions === 'object') {
      this.rtcOptions = rtcOptions;
    }

    if (offerOptions !== undefined) {
      this.offerOptions = { ...this.offerOptions, ...offerOptions };
    }

    if (undefined !== connectTimeout) {
      if ('number' === typeof connectTimeout && connectTimeout >= 0) {
        this.connectTimeout = connectTimeout;
      } else {
        logger.warn('connectTimeout must be a number >= 0');
      }
    }

    if (undefined !== fetchTimeout) {
      if ('number' === typeof fetchTimeout && fetchTimeout >= 0) {
        this.fetchTimeout = fetchTimeout;
      } else {
        logger.warn('fetchTimeout must be a number >= 0');
      }
    }

    if (undefined !== connectRetryDelay) {
      if ('number' === typeof connectRetryDelay && connectRetryDelay >= 0 && connectRetryDelay <= 10) {
        this.connectRetry.delay = connectRetryDelay;
      } else {
        logger.warn('connectRetryDelay must be a number between 0 and 10');
      }
    }

    if (undefined !== maxConnectRetry) {
      if ('number' === typeof maxConnectRetry && maxConnectRetry >= 0 && maxConnectRetry <= 10) {
        this.connectRetry.maxNum = maxConnectRetry;
      } else {
        logger.warn('maxConnectRetry must be a number between 0 and 10');
      }
    }

    if (undefined !== maxDecodeFailCount) {
      if ('number' === typeof maxDecodeFailCount && maxDecodeFailCount >= 0) {
        this.streamDecodeFail.maxNum = maxDecodeFailCount;
      } else {
        logger.warn('maxDecodeFailCount must be a number >= 0');
      }
    }

    if (undefined !== maxReceiveFailCount) {
      if ('number' === typeof maxReceiveFailCount && maxReceiveFailCount >= 0) {
        this.streamReceiveFail.maxNum = maxReceiveFailCount;
      } else {
        logger.warn('maxReceiveFailCount must be a number >= 0');
      }
    }

    if (undefined !== signalController) {
      if ('string' === typeof signalController && ['tencent', 'gbs'].includes(signalController)) {
        if ('gbs' === signalController) {
          this.signalController = new GbsSignalController({
            env: this.env as any,
            fetchTimeout: this.fetchTimeout,
            fetchSetup,
          });
        } else if ('tencent' === signalController) {
          this.signalController = new TencentSignalController({
            env: this.env as any,
            fetchTimeout: this.fetchTimeout,
            fetchSetup,
          });
        }
      } else if (signalController instanceof SignalController) {
        this.signalController = signalController;
      } else {
        logger.warn('signalController must be "tencent", "gbs" or an instance of SignalController');
      }
    }

    logger.debug(`setConfig:`, config);
  }

  attachMediaElement(mediaElement: string | HTMLMediaElement) {
    if (this.mediaElement) {
      logger.warn('already attached a media element, please destroy first');
      return;
    }

    const view = typeof mediaElement === 'string' ? document.getElementById(mediaElement) : mediaElement;
    if (view && view instanceof HTMLMediaElement) {
      this.mediaElement = view;
    } else {
      logger.warn('HTMLMediaElement or Element id is required');
    }
  }

  async startPlay(url: string) {
    if (!this.mediaElement) {
      throw new Error('HTMLMediaElement must be attached before startPlay()');
    }
    if (!this.signalController) {
      throw new Error('signalController is required');
    }
    if (typeof url === 'string') {
      url = url.trim();
    }
    if (!/^(webrtc:\/\/)/i.test(url) && !/\.sdp/i.test(url)) {
      throw new Error(`invalid webrtc url: ${url}`);
    }
    if (!this.offerOptions.offerToReceiveAudio && !this.offerOptions.offerToReceiveVideo) {
      logger.warn('Both offerToReceiveAudio and offerToReceiveVideo are false!');
      return;
    }
    this.playUrl = url;
    this.streamDecodeFail.curNum = 0;
    this.streamReceiveFail.curNum = 0;
    this.playStatus = PlayStatus.INIT;

    logger.debug(`startPlay: ${url}`);

    if (this.mediaElement.autoplay) {
      this.connectRetry.playing = true;
    }

    if (!this.rtcConnection) {
      this.rtcConnection = new WebRTCConnection({
        onSetLocalDescription: this.onSetLocalDescription,
        onTrack: this.onTrack,
        onConnect: this.onConnect,
        onError: this.onError,
        onDisconnect: this.onDisconnect,
      });
    }

    let rtcOptions = {};
    try {
      rtcOptions = await this.signalController.fetchPeerConnectionConfig(url);
    } catch (err) {
      logger.warn(`url: ${url} fetchPeerConnectionConfig fail:`, err);
    }

    const offerConfig = { ...this.offerOptions };
    if (typeof offerConfig.sdpTransform !== 'function') {
      offerConfig.sdpTransform = this.signalController.sdpTransform.bind(this.signalController);
    }

    this.rtcConnection.initPeerConnection({
      rtcConfig: Object.assign(this.rtcOptions || {}, rtcOptions),
      offerConfig,
    });
  }

  destroy() {
    this.emit(PlayerEvents.DESTROY);
    this.removeAllListeners();

    if (this.connectTimeoutId) {
      clearTimeout(this.connectTimeoutId);
      this.connectTimeoutId = null;
    }

    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    if (this.statsIntervalId) {
      clearInterval(this.statsIntervalId);
      this.statsIntervalId = null;
    }

    this.connectRetry.curNum = 0;
    this.connectRetry.playing = false;

    if (this.playUrl) {
      this.signalController.fetchStopStream(this.playUrl).catch(console.log);
      this.playUrl = null;
    }

    this.signalController?.abort('destroy');
    this.rtcConnection?.disconnect();
    this.lastStatsReport = null;

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.mediaStream = null;
    }

    if (this.mediaElement) {
      this.mediaElement.pause();
      this.mediaElement.srcObject = null;
      this.mediaElement.load();
      this.mediaElement = null;
    }
  }

  private onSetLocalDescription = async (offer: RTCSessionDescriptionInit) => {
    try {
      this.emit(PlayerEvents.PULL_START, offer.sdp);

      let answer: RTCSessionDescriptionInit = null;
      if (/\.sdp/i.test(this.playUrl)) {
        answer = await this.signalController.fetchStreamSdp(offer, this.playUrl);
      } else {
        answer = await this.signalController.fetchStreamAnswer(offer, this.playUrl);
      }

      this.emit(PlayerEvents.PULL_SUCCESS, answer.sdp);

      this.rtcConnection.setRemoteDescription(answer);

      logger.info(`url: ${this.playUrl} start:`, Date.now());

      this.connectTimeoutId && clearTimeout(this.connectTimeoutId);
      this.connectTimeoutId = setTimeout(() => {
        logger.warn(`url: ${this.playUrl} connect timeout, ts:`, Date.now());
        this.rtcConnection.disconnect({ code: DISCONNECT_CODE.RECONNECT, msg: 'connect timeout' });
      }, this.connectTimeout);
    } catch (err) {
      console.warn(err);
      this.rtcConnection.disconnect();
      this.emit(PlayerEvents.ERROR, {
        code: ErrorCode.PULL_STREAM_FAIL,
        message: err.message,
      });
    }
  };

  private onTrack = (track: MediaStreamTrack) => {
    if (this.mediaStream) {
      this.mediaStream.addTrack(track);
    } else {
      this.mediaStream = new MediaStream();
      this.mediaStream.addTrack(track);
      this.mediaElement.srcObject = this.mediaStream;
    }
    logger.debug(`url: ${this.playUrl} current track length:`, this.mediaStream.getTracks().length);

    if (this.mediaElement.autoplay && this.connectRetry.playing && this.mediaElement.paused) {
      this.mediaElement.play().catch((err) => console.log(err));
    }
  };

  private onError = (msg: string) => {
    logger.warn(`url: ${this.playUrl} error:`, msg);

    this.emit(PlayerEvents.ERROR, {
      code: ErrorCode.WEBRTC_FAIL,
      message: msg,
    });
  };

  private onDisconnect = (reason: { code: DISCONNECT_CODE; msg: string }) => {
    this.lastStatsReport = null;
    this.streamReceiveFail.curNum = 0;
    this.streamDecodeFail.curNum = 0;
    this.playStatus = PlayStatus.INIT;

    if (this.connectTimeoutId) {
      clearTimeout(this.connectTimeoutId);
      this.connectTimeoutId = null;
    }
    if (this.statsIntervalId) {
      clearInterval(this.statsIntervalId);
      this.statsIntervalId = null;
    }

    if (reason.code === DISCONNECT_CODE.RECONNECT) {
      this.connectRetry.playing = !this.mediaElement.paused;
      if (this.playUrl) {
        this.signalController.fetchStopStream(this.playUrl).catch(console.log);
      }
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => {
          track.stop();
        });
        this.mediaStream = null;
      }
      if (this.mediaElement) {
        this.mediaElement.pause();
      }
      if (this.retryTimeoutId) {
        clearTimeout(this.retryTimeoutId);
        this.retryTimeoutId = null;
      }
      this.retryTimeoutId = setTimeout(() => {
        this.connectRetry.curNum++;
        const { curNum, maxNum } = this.connectRetry;

        if (curNum <= maxNum) {
          logger.info(`url: ${this.playUrl} current reconnect num: ${curNum}, max reconnect num: ${maxNum}`);

          this.emit(PlayerEvents.RECONNECT, curNum, maxNum);
          this.startPlay(this.playUrl);
        } else {
          logger.warn(`url: ${this.playUrl} reached max reconnect num: ${maxNum}`);

          this.emit(PlayerEvents.ERROR, {
            code: ErrorCode.STREAM_DISCONNECT,
            message: 'reached max reconnect num',
          });
          this.connectRetry.curNum = 0;
          this.connectRetry.playing = false;
        }
      }, this.connectRetry.delay * 1000);
    }
  };

  private onStats(stats: RTCStatsReport) {
    const statsReport = formatStatsReport(stats, this.lastStatsReport);
    if (statsReport) {
      statsReport.video.frameWidth = statsReport.video.frameWidth || (this.mediaElement as HTMLVideoElement)?.videoWidth;
      statsReport.video.frameHeight = statsReport.video.frameHeight || (this.mediaElement as HTMLVideoElement)?.videoHeight;
      this.emit(PlayerEvents.STATS, statsReport);
    }
    this.lastStatsReport = stats;

    if (statsReport?.video && this.offerOptions.offerToReceiveVideo) {
      const { bitrate, framesPerSecond, framesDecoded, framesDecodedPerSecond } = statsReport.video;
      if (bitrate && framesPerSecond) {
        this.connectRetry.curNum = 0;
      }
      if (!this.mediaElement) {
        return;
      }
      if (bitrate && framesPerSecond) {
        this.streamReceiveFail.curNum = 0;
      } else {
        this.streamReceiveFail.curNum += 1;
        if (this.streamReceiveFail.curNum >= this.streamReceiveFail.maxNum) {
          this.emit(PlayerEvents.EMPTY);
          this.streamReceiveFail.curNum = 0;
        }
      }
      if (bitrate && framesPerSecond && !framesDecoded) {
        this.streamDecodeFail.curNum += 1;
        if (this.streamDecodeFail.curNum === this.streamDecodeFail.maxNum) {
          logger.warn(`url: ${this.playUrl} stream decode failed`);

          this.emit(PlayerEvents.ERROR, {
            code: ErrorCode.DECODE_FAIL,
            message: 'decode failed',
            bitrate,
            framesPerSecond,
          });
        }
      } else {
        this.streamDecodeFail.curNum = 0;
      }

      if (framesDecodedPerSecond >= 0 || framesPerSecond >= 0) {
        if (framesDecodedPerSecond <= 5 || framesPerSecond <= 5) {
          if (this.playStatus !== PlayStatus.WAITING) {
            logger.info(`url: ${this.playUrl} frame waiting`);

            this.playStatus = PlayStatus.WAITING;
            this.emit(PlayerEvents.WAITING);
          }
        } else {
          if (this.playStatus === PlayStatus.WAITING) {
            this.emit(PlayerEvents.WAITING_STOP);
          }
          this.playStatus = PlayStatus.PLAYING;
        }
      }
    }
  }

  private async startStat() {
    logger.info(`url: ${this.playUrl} startStat`);

    if (this.statsIntervalId) {
      clearInterval(this.statsIntervalId);
      this.statsIntervalId = null;
    }
    const stats = await this.rtcConnection.getStats();
    if (stats) {
      this.onStats(stats);
    }
    this.statsIntervalId = setInterval(() => {
      this.rtcConnection?.getStats().then((stats) => {
        stats && this.onStats(stats);
      });
    }, 1000);
  }

  private onConnect = () => {
    if (this.connectTimeoutId) {
      clearTimeout(this.connectTimeoutId);
      this.connectTimeoutId = null;
    }
    this.startStat();
    this.emit(PlayerEvents.CONNECTED);
  };
}
